import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { icons } from '../../components/ui/Icons';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Avatar } from '../../components/ui/Avatar';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { UserWithDetails } from '../../types/auth';
import { formatDate } from '../../lib/date';

const API_BASE = '/api/admin';

type UserStatusFilter = 'all' | 'active' | 'inactive';
type UserSortField = 'name' | 'email' | 'status' | 'lastLoginAt' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface ApiErrorBody {
  message?: string;
  error?: string;
}

class RequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
  }
}

const USER_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle Status' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
];

const USER_SORT_OPTIONS: Array<{ value: UserSortField; label: string }> = [
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'E-Mail' },
  { value: 'status', label: 'Status' },
  { value: 'lastLoginAt', label: 'Letzter Login' },
  { value: 'createdAt', label: 'Erstellt' },
];

async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as ApiErrorBody;
      message = body.message || body.error || message;
    } catch {
      message = `HTTP ${response.status}`;
    }
    throw new RequestError(response.status, message);
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Die API hat keine gültigen JSON-Daten zurückgegeben.');
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof RequestError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function normalizeUser(value: unknown): UserWithDetails {
  const data = value as Partial<UserWithDetails>;
  const rawRoleSummaries = (value as { discordRoleSummaries?: unknown }).discordRoleSummaries;
  const rawRoles = rawRoleSummaries ?? (value as { discordRoles?: unknown }).discordRoles;
  const rawPermissionKeys = (value as { permissionKeys?: unknown }).permissionKeys;

  return {
    id: data.id || '',
    discordUserId: data.discordUserId || '',
    discordUsername: data.discordUsername || '',
    discordDisplayName: data.discordDisplayName ?? null,
    discordAvatar: data.discordAvatar ?? null,
    email: data.email ?? null,
    firstName: data.firstName ?? null,
    lastName: data.lastName ?? null,
    personnelNumber: data.personnelNumber ?? null,
    departmentId: data.departmentId ?? null,
    departmentName: data.departmentName ?? null,
    positionId: data.positionId ?? null,
    position: data.position ?? null,
    isActive: data.isActive ?? false,
    lastLoginAt: data.lastLoginAt ?? null,
    createdAt: data.createdAt || '',
    updatedAt: data.updatedAt || '',
    discordRoles: Array.isArray(rawRoles)
      ? rawRoles.map((role) => {
          if (typeof role === 'string') return { id: role, name: role };
          const item = role as Partial<{ id: string; name: string; discordRoleId: string }>;
          const id = item.id || item.discordRoleId || '';
          return { id, name: item.name || id || 'Unbekannte Rolle' };
        })
      : [],
    effectivePermissions: [],
    permissionKeys: Array.isArray(rawPermissionKeys)
      ? rawPermissionKeys.filter((key): key is string => typeof key === 'string')
      : [],
    overrides: [],
  };
}

function compareUsers(a: UserWithDetails, b: UserWithDetails, field: UserSortField): number {
  let result = 0;
  switch (field) {
    case 'email':
      result = (a.email || '').localeCompare(b.email || '', 'de-DE');
      break;
    case 'status':
      result = Number(a.isActive) - Number(b.isActive);
      break;
    case 'lastLoginAt':
      result = new Date(a.lastLoginAt || '').getTime() - new Date(b.lastLoginAt || '').getTime();
      break;
    case 'createdAt':
      result = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      break;
    case 'name':
    default:
      result = (a.discordDisplayName || a.discordUsername).localeCompare(
        b.discordDisplayName || b.discordUsername,
        'de-DE',
      );
  }
  if (result === 0) result = a.discordUsername.localeCompare(b.discordUsername, 'de-DE');
  return result;
}

export function UsersListPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>('all');
  const [sortField, setSortField] = useState<UserSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const requestRef = useRef<AbortController | null>(null);

  const fetchUsers = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await requestJson<unknown[]>(`/users`, { signal: controller.signal });
      setUsers(Array.isArray(data) ? data.map(normalizeUser) : []);
    } catch (error) {
      if (!isAbortError(error)) {
        setError(getErrorMessage(error, 'Benutzer konnten nicht geladen werden.'));
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
    }
  }, []);

  const updateUserStatus = useCallback(async (user: UserWithDetails, isActive: boolean) => {
    try {
      await requestJson<{ success: boolean }>(`/users/${encodeURIComponent(user.id)}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive }),
      });
      await fetchUsers();
    } catch (error) {
      alert(getErrorMessage(error, 'Benutzerstatus konnte nicht geändert werden.'));
    }
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const result = users.filter((user) => {
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.isActive) ||
        (statusFilter === 'inactive' && !user.isActive);
      const searchable = [
        user.discordUsername,
        user.discordDisplayName,
        user.email,
        user.personnelNumber,
        user.position,
        user.departmentName,
      ]
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .toLowerCase();
      return matchesStatus && (!query || searchable.includes(query));
    });

    result.sort((a, b) => {
      const comparison = compareUsers(a, b, sortField);
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [users, search, statusFilter, sortField, sortDirection]);

  useEffect(() => {
    fetchUsers();
    return () => requestRef.current?.abort();
  }, [fetchUsers]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Benutzer & Zugriff', href: '/admin/access' },
    { label: 'Benutzer', current: true },
  ];

  const columns = useMemo(() => [
    {
      key: 'user',
      header: 'Benutzer',
      className: 'min-w-[240px]',
      render: (user: UserWithDetails) => {
        const avatarSrc = user.discordUserId && user.discordAvatar
          ? `https://cdn.discordapp.com/avatars/${encodeURIComponent(user.discordUserId)}/${encodeURIComponent(user.discordAvatar)}.png`
          : null;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar src={avatarSrc} name={user.discordDisplayName || user.discordUsername} size="sm" />
            <div className="min-w-0">
              <p className="truncate font-medium text-neutral-900 dark:text-white">
                {user.discordDisplayName || user.discordUsername}
              </p>
              <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">@{user.discordUsername}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'email',
      header: 'Kontakt',
      render: (user: UserWithDetails) => (
        <div className="min-w-[140px]">
          <p className="truncate text-sm text-neutral-900 dark:text-gray-100">{user.email || '-'}</p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{user.discordUserId || '-'}</p>
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      className: 'w-28',
      render: (user: UserWithDetails) => (
        <Badge variant={user.isActive ? 'medical' : 'gray'}>{user.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
      ),
    },
    {
      key: 'personnel',
      header: 'Personal',
      className: 'min-w-[180px]',
      render: (user: UserWithDetails) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-neutral-900 dark:text-gray-100">{user.personnelNumber || '-'}</p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {[user.position, user.departmentName].filter(Boolean).join(' · ') || '-'}
          </p>
        </div>
      ),
    },
    {
      key: 'lastLoginAt',
      header: 'Letzter Login',
      className: 'w-36',
      render: (user: UserWithDetails) => <span className="whitespace-nowrap text-sm text-neutral-700 dark:text-neutral-300">{formatDate(user.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: 'Aktionen',
      className: 'w-32',
      render: (user: UserWithDetails) => (
        <Button
          variant="outline"
          size="sm"
          leftIcon={user.isActive ? 'UserX' : 'UserCheck'}
          onClick={(event) => {
            event.stopPropagation();
            updateUserStatus(user, !user.isActive);
          }}
        >
          {user.isActive ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
      ),
    },
  ], [updateUserStatus]);

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Benutzerverwaltung</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Konten suchen, nach Status und Aktivität sortieren und Details einsehen.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="primary" leftIcon="UserPlus" asChild>
            <Link to="/admin/access/users/new">Neuer Benutzer</Link>
          </Button>
          <Button variant="outline" leftIcon="RefreshCw" loading={loading} onClick={fetchUsers}>
            Aktualisieren
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardHeader className="mb-4">
          <CardTitle>Benutzerliste</CardTitle>
          <CardDescription>
            {filteredUsers.length} von {users.length} Benutzern
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && users.length === 0 ? (
            <ErrorState description={error} onRetry={fetchUsers} />
          ) : error ? (
            <Alert variant="error" title="Benutzer konnten nicht vollständig geladen werden" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_4rem]">
            <Input
              aria-label="Benutzer suchen"
              placeholder="Name, Discord-Name, E-Mail, Personalnummer ..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              leftIcon="Search"
            />
            <Select
              aria-label="Status filtern"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as UserStatusFilter)}
              options={USER_STATUS_OPTIONS}
            />
            <Select
              aria-label="Benutzer sortieren"
              value={sortField}
              onChange={(event) => setSortField(event.target.value as UserSortField)}
              options={USER_SORT_OPTIONS}
            />
            <Button
              variant="outline"
              size="sm"
              aria-label="Sortierrichtung wechseln"
              onClick={() => setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'))}
            >
              {sortDirection === 'asc' ? <icons.ChevronUp className="h-4 w-4" /> : <icons.ChevronDown className="h-4 w-4" />}
            </Button>
          </div>

          {loading && users.length === 0 ? (
            <SkeletonTable rows={6} columns={6} />
          ) : filteredUsers.length === 0 ? (
            <EmptyState
              icon="Users"
              title="Keine Benutzer gefunden"
              description="Passende Benutzer sind nicht vorhanden oder die aktuellen Filter liefern keine Ergebnisse."
              action={
                <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                  Filter zurücksetzen
                </Button>
              }
            />
          ) : (
            <Table
              columns={columns}
              data={filteredUsers}
              keyExtractor={(user) => user.id}
              rowClick={(user) => navigate(`/admin/access/users/${user.id}`)}
              rowActionLabel={(user) => `Details für ${user.discordDisplayName || user.discordUsername}`}
              loading={loading && users.length > 0}
              emptyMessage="Keine Benutzer entsprechen den aktuellen Filtern."
              striped
              hoverable
              compact
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}